import Phaser from 'phaser';
import {
  ComponentType,
  ComponentCategory,
  ComponentDef,
  COMPONENT_DEFS,
  ALL_COMPONENT_TYPES,
  getEvolutionCost,
  getEvolutionMultiplier,
  EVOLUTION_STAT_MULTIPLIER,
} from '@/components/ComponentDefs';
import { TILE_SIZE, CANVAS_WIDTH, CANVAS_HEIGHT } from '@/config/GameConfig';
import { SoundManager } from '@/systems/SoundManager';
import { createComponentVisual, CATEGORY_COLORS, getComponentTextureKeyAtLevel } from '@/systems/ComponentIcons';
import { ProgressionManager } from '@/systems/ProgressionManager';
import { TECH, drawTechRect, createSmallTechButton } from '@/ui/TechUI';
import { GOD } from '@/config/GodMode';
import { JuiceEffects } from '@/systems/JuiceEffects';
import { getEvolutionResourceCost } from '@/config/ComponentEvolution';
import { ResourceManager } from '@/systems/ResourceManager';
import { t } from '@/i18n/I18n';

// Palette layout constants
const PALETTE_X = 10;
const PALETTE_CARD_W = 110;
const PALETTE_CARD_H = 100;
const PALETTE_GAP = 8;
const CATEGORY_GAP = 24;
const ARROW_W = 28;
const PALETTE_VISIBLE_W = CANVAS_WIDTH - PALETTE_X * 2 - ARROW_W * 2 - 10;
const PALETTE_HEADER_H = 18;
const PALETTE_TOTAL_H = PALETTE_HEADER_H + PALETTE_CARD_H + 12;

export interface PaletteCallbacks {
  getAvailableFunds: () => number;
  spawnCostAnim: (amount: number, worldX: number, worldY: number) => void;
  showCampaignHint: (localX: number, localY: number) => void;
  showTooltip: (x: number, y: number, def: ComponentDef, locked?: boolean, evoLevel?: number) => void;
  hideTooltip: () => void;
  snapshotEditData: () => object;
}

export class PaletteController {
  // Palette scroll
  paletteContainer!: Phaser.GameObjects.Container;
  paletteScrollX = 0;
  paletteContentW = 0;
  private paletteArrowL: Phaser.GameObjects.Text | null = null;
  private paletteArrowR: Phaser.GameObjects.Text | null = null;
  private arrowGlowTweenL: Phaser.Tweens.Tween | null = null;
  private arrowGlowTweenR: Phaser.Tweens.Tween | null = null;
  private paletteSwiping = false;
  private paletteSwipeLastX = 0;
  private paletteMaskGfx: Phaser.GameObjects.Graphics | null = null;
  private paletteBgRect: Phaser.GameObjects.Rectangle | null = null;
  private paletteInputBound = false;
  unlockAnimPlaying = false;
  paletteHighlights: Map<string, Phaser.GameObjects.Rectangle> = new Map();
  selectedType: ComponentType | null = null;
  selectedEvoLevel: number = 0;
  dragGhost: Phaser.GameObjects.Graphics | Phaser.GameObjects.Sprite | null = null;
  dragType: ComponentType | null = null;
  dragEvoLevel: number = 0;

  // Upgrade dialog
  upgradeDialog: Phaser.GameObjects.Container | null = null;

  // Currency display
  currencyText!: Phaser.GameObjects.Text;
  workshopCoinIcon!: Phaser.GameObjects.Image;

  // Cannot afford feedback
  private lockedFailStreak = 0;

  private paletteRebuildPending = false;

  private paletteY: number;

  constructor(
    private scene: Phaser.Scene,
    private isCampaign: boolean,
    private callbacks: PaletteCallbacks,
    paletteY: number,
  ) {
    this.paletteY = paletteY;
  }

  setup(): void {
    // No EventBus listeners — PaletteController events are wired by WorkshopScene
  }

  destroy(): void {
    this.closeUpgradeDialog();
    if (this.dragGhost) {
      this.dragGhost.destroy();
      this.dragGhost = null;
    }
  }

  // ══════════════════════════════════════════
  //  CURRENCY DISPLAY
  // ══════════════════════════════════════════

  updateCurrencyDisplay(): void {
    this.currencyText?.setText(`${this.callbacks.getAvailableFunds()}`);
  }

  /** Currency changed — update display + schedule palette rebuild */
  onCurrencyChanged = (): void => {
    this.updateCurrencyDisplay();
    this.schedulePaletteRebuild();
  };

  // ══════════════════════════════════════════
  //  PALETTE BUILD/REBUILD
  // ══════════════════════════════════════════

  /** Schedule a palette rebuild on next frame — deduplicates multiple events in the same tick */
  schedulePaletteRebuild = (): void => {
    if (this.paletteRebuildPending) return;
    if (this.unlockAnimPlaying) return;
    this.paletteRebuildPending = true;
    this.scene.time.delayedCall(0, () => {
      this.paletteRebuildPending = false;
      this.rebuildPalette();
    });
  };

  rebuildPalette = (): void => {
    const savedScrollX = this.paletteScrollX;

    if (this.paletteContainer) {
      this.paletteContainer.setMask(null as any);
      this.paletteContainer.destroy(true);
    }
    if (this.paletteMaskGfx) { this.paletteMaskGfx.destroy(); this.paletteMaskGfx = null; }
    if (this.paletteBgRect) { this.paletteBgRect.destroy(); this.paletteBgRect = null; }
    if (this.arrowGlowTweenL) { this.arrowGlowTweenL.destroy(); this.arrowGlowTweenL = null; }
    if (this.arrowGlowTweenR) { this.arrowGlowTweenR.destroy(); this.arrowGlowTweenR = null; }
    if (this.paletteArrowL) { this.paletteArrowL.destroy(); this.paletteArrowL = null; }
    if (this.paletteArrowR) { this.paletteArrowR.destroy(); this.paletteArrowR = null; }

    this.paletteScrollX = 0;
    this.createPalette();
    if (savedScrollX !== 0) {
      this.scrollPalette(savedScrollX);
    }
  };

  createPalette(): void {
    const categories: { cat: ComponentCategory; label: string; types: ComponentType[] }[] = [
      { cat: ComponentCategory.PASSIVE, label: 'Base', types: [] },
      { cat: ComponentCategory.MOVEMENT, label: 'Movement', types: [] },
      { cat: ComponentCategory.SENSOR, label: 'Sensors', types: [] },
      { cat: ComponentCategory.WEAPON, label: 'Weapons', types: [] },
      { cat: ComponentCategory.SUPPORT, label: 'Support', types: [] },
    ];
    for (const t of ALL_COMPONENT_TYPES) {
      if (!ProgressionManager.isComponentVisible(t)) continue;
      const def = COMPONENT_DEFS[t];
      const group = categories.find(g => g.cat === def.category);
      if (group) group.types.push(t);
    }

    const allCards: { type: ComponentType; cat: ComponentCategory; evoLevel: number }[] = [];
    for (const g of categories) {
      for (const t of g.types) {
        const maxTier = ProgressionManager.getComponentLevel(t);
        for (let lvl = 0; lvl <= maxTier; lvl++) {
          allCards.push({ type: t, cat: g.cat, evoLevel: lvl });
        }
      }
    }

    let contentW = 0;
    {
      let prevCat: ComponentCategory | null = null;
      for (let i = 0; i < allCards.length; i++) {
        if (i > 0) contentW += PALETTE_GAP;
        if (allCards[i].cat !== prevCat && prevCat !== null) contentW += CATEGORY_GAP;
        prevCat = allCards[i].cat;
        contentW += PALETTE_CARD_W;
      }
    }
    this.paletteContentW = contentW;

    const fullW = CANVAS_WIDTH - PALETTE_X * 2;
    this.paletteBgRect = this.scene.add.rectangle(CANVAS_WIDTH / 2, this.paletteY + PALETTE_TOTAL_H / 2, fullW + 10, PALETTE_TOTAL_H, 0x0d0d1a, 0.85)
      .setStrokeStyle(1, 0x333366, 0.5).setDepth(1);

    this.paletteContainer = this.scene.add.container(0, 0).setDepth(2);

    const gfx = this.scene.add.graphics().setDepth(2);
    this.paletteContainer.add(gfx);

    this.paletteMaskGfx = this.scene.make.graphics({});
    this.paletteMaskGfx.fillRect(PALETTE_X + ARROW_W, this.paletteY - 2, PALETTE_VISIBLE_W, PALETTE_TOTAL_H + 4);
    this.paletteContainer.setMask(this.paletteMaskGfx.createGeometryMask());

    const startX = PALETTE_X + ARROW_W + 4;
    const labelY = this.paletteY + 4;
    const cardY = labelY + PALETTE_HEADER_H;

    // First pass: compute category group positions
    const groups: { cat: ComponentCategory; startX: number; endX: number }[] = [];
    let x = startX;
    let prevCat: ComponentCategory | null = null;
    let groupStartX = startX;

    for (let i = 0; i < allCards.length; i++) {
      if (allCards[i].cat !== prevCat) {
        if (prevCat !== null) {
          groups.push({ cat: prevCat, startX: groupStartX, endX: x - PALETTE_GAP });
          x += CATEGORY_GAP;
        }
        prevCat = allCards[i].cat;
        groupStartX = x;
      } else if (i > 0) {
        x += PALETTE_GAP;
      }
      x += PALETTE_CARD_W;
    }
    if (prevCat !== null) {
      groups.push({ cat: prevCat, startX: groupStartX, endX: x });
    }

    // Category labels
    for (const group of groups) {
      const g = categories.find(c => c.cat === group.cat);
      if (!g) continue;
      const catColor = CATEGORY_COLORS[g.cat] ?? 0x888888;
      const centerX = (group.startX + group.endX) / 2;
      const groupW = group.endX - group.startX;

      const barBg = this.scene.add.rectangle(centerX, labelY + 6, groupW, 14, catColor, 0.08);
      this.paletteContainer.add(barBg);

      const label = this.scene.add.text(centerX, labelY + 6, g.label.toUpperCase(), {
        fontSize: '10px', color: `#${catColor.toString(16).padStart(6, '0')}`,
        fontFamily: 'monospace', fontStyle: 'bold',
      }).setOrigin(0.5, 0.5);
      this.paletteContainer.add(label);
    }

    // Dividers
    for (let i = 1; i < groups.length; i++) {
      const divX = (groups[i - 1].endX + groups[i].startX) / 2;
      gfx.lineStyle(1, 0x333366, 0.4);
      gfx.lineBetween(divX, labelY - 1, divX, cardY + PALETTE_CARD_H + 2);
    }

    // Place cards
    x = startX;
    prevCat = null;
    for (let i = 0; i < allCards.length; i++) {
      if (allCards[i].cat !== prevCat) {
        if (prevCat !== null) x += CATEGORY_GAP;
        prevCat = allCards[i].cat;
      } else if (i > 0) {
        x += PALETTE_GAP;
      }
      this.createPaletteCard(allCards[i].type, x, cardY, allCards[i].evoLevel);
      x += PALETTE_CARD_W;
    }

    // Scroll arrows
    const arrowY = this.paletteY + PALETTE_TOTAL_H / 2;
    const canScroll = this.paletteContentW > PALETTE_VISIBLE_W;

    if (canScroll) {
      this.paletteArrowL = this.scene.add.text(PALETTE_X + ARROW_W / 2, arrowY, '\u25C0', {
        fontSize: '18px', color: '#8888aa', fontFamily: 'monospace',
      }).setOrigin(0.5).setDepth(3).setInteractive({ useHandCursor: true });
      this.paletteArrowL.on('pointerdown', () => this.scrollPalette(PALETTE_CARD_W + PALETTE_GAP));
      this.paletteArrowL.on('pointerover', () => this.paletteArrowL?.setColor('#ffffff'));
      this.paletteArrowL.on('pointerout', () => this.paletteArrowL?.setColor('#8888aa'));

      this.paletteArrowR = this.scene.add.text(CANVAS_WIDTH - PALETTE_X - ARROW_W / 2, arrowY, '\u25B6', {
        fontSize: '18px', color: '#8888aa', fontFamily: 'monospace',
      }).setOrigin(0.5).setDepth(3).setInteractive({ useHandCursor: true });
      this.paletteArrowR.on('pointerdown', () => this.scrollPalette(-(PALETTE_CARD_W + PALETTE_GAP)));
      this.paletteArrowR.on('pointerover', () => this.paletteArrowR?.setColor('#ffffff'));
      this.paletteArrowR.on('pointerout', () => this.paletteArrowR?.setColor('#8888aa'));

      this.arrowGlowTweenL = this.scene.tweens.add({
        targets: this.paletteArrowL, alpha: { from: 0.4, to: 1 },
        duration: 900, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
        paused: true,
      });
      this.arrowGlowTweenR = this.scene.tweens.add({
        targets: this.paletteArrowR, alpha: { from: 0.4, to: 1 },
        duration: 900, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
      this.paletteArrowL.setAlpha(0.25);
    }

    // Input listeners for scroll — only register once
    if (!this.paletteInputBound) {
      this.paletteInputBound = true;
      this.scene.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gos: unknown, dx: number, dy: number) => {
        if (_pointer.y >= this.paletteY) {
          const delta = Math.abs(dx) > Math.abs(dy) ? -dx : -dy;
          this.scrollPalette(delta * 0.5);
        }
      });
      this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        if (pointer.y >= this.paletteY) {
          this.paletteSwiping = true;
          this.paletteSwipeLastX = pointer.x;
        }
      });
      this.scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
        if (this.paletteSwiping && pointer.isDown && !this.dragGhost) {
          const dx = pointer.x - this.paletteSwipeLastX;
          if (Math.abs(dx) > 2) {
            this.scrollPalette(dx);
            this.paletteSwipeLastX = pointer.x;
          }
        }
      });
      this.scene.input.on('pointerup', () => {
        this.paletteSwiping = false;
      });
    }
  }

  scrollPalette(deltaX: number): void {
    this.paletteScrollX += deltaX;
    const maxScroll = 0;
    const minScroll = -Math.max(0, this.paletteContentW - PALETTE_VISIBLE_W);
    this.paletteScrollX = Math.max(minScroll, Math.min(maxScroll, this.paletteScrollX));
    this.paletteContainer.x = this.paletteScrollX;

    const canScrollLeft = this.paletteScrollX < maxScroll;
    const canScrollRight = this.paletteScrollX > minScroll;
    if (canScrollLeft) {
      if (this.arrowGlowTweenL?.isPaused()) this.arrowGlowTweenL.resume();
    } else {
      this.arrowGlowTweenL?.pause();
      this.paletteArrowL?.setAlpha(0.25);
    }
    if (canScrollRight) {
      if (this.arrowGlowTweenR?.isPaused()) this.arrowGlowTweenR.resume();
    } else {
      this.arrowGlowTweenR?.pause();
      this.paletteArrowR?.setAlpha(0.25);
    }
  }

  private createPaletteCard(type: ComponentType, x: number, y: number, evoLevel = 0): void {
    const def = COMPONENT_DEFS[type];
    const catColor = CATEGORY_COLORS[def.category] ?? 0x888888;
    const locked = !ProgressionManager.isComponentUnlocked(type);

    const bgColor = locked ? 0x0e0e1a : 0x151530;
    const bg = this.scene.add.rectangle(x + PALETTE_CARD_W / 2, y + PALETTE_CARD_H / 2, PALETTE_CARD_W, PALETTE_CARD_H, bgColor, locked ? 0.7 : 0.95)
      .setStrokeStyle(1, locked ? 0x222233 : 0x333355, 0.6);
    bg.setInteractive({ useHandCursor: !locked, draggable: !locked });
    this.paletteContainer.add(bg);

    const bar = this.scene.add.rectangle(x + PALETTE_CARD_W / 2, y + 2, PALETTE_CARD_W - 4, 2, catColor, locked ? 0.2 : 0.6);
    this.paletteContainer.add(bar);

    const iconVisual = createComponentVisual(this.scene, type, 40, locked ? 0x333344 : def.color, evoLevel);
    iconVisual.setPosition(x + PALETTE_CARD_W / 2, y + 32);
    if (locked) (iconVisual as Phaser.GameObjects.Graphics).setAlpha(0.4);
    this.paletteContainer.add(iconVisual as Phaser.GameObjects.Graphics);

    if (locked) {
      const lockImg = this.scene.add.image(x + PALETTE_CARD_W / 2, y + 30, 'icon_blocked')
        .setDisplaySize(56, 56).setOrigin(0.5);
      this.paletteContainer.add(lockImg);

      const unlockPrice = Math.round(def.unlockCost * GOD.workshopPriceMult);
      const costLabel = this.scene.add.text(x + PALETTE_CARD_W / 2, y + 62, GOD.freeBuilding ? 'FREE' : `$${unlockPrice}`, {
        fontSize: '11px', color: '#ff8844', fontFamily: 'monospace', fontStyle: 'bold',
      }).setOrigin(0.5, 0);
      this.paletteContainer.add(costLabel);

      const lockedCompName = t(`component.${def.type}.name`);
      const shortName = lockedCompName.length > 12 ? lockedCompName.substring(0, 11) + '.' : lockedCompName;
      const nameText = this.scene.add.text(x + PALETTE_CARD_W / 2, y + 78, shortName, {
        fontSize: '10px', color: '#9999aa', fontFamily: 'monospace',
      }).setOrigin(0.5, 0);
      this.paletteContainer.add(nameText);

      const lockedElements = [bg, bar, iconVisual, lockImg, costLabel, nameText];
      bg.on('pointerdown', () => {
        if (!this.isCampaign) {
          SoundManager.play('error');
          this.callbacks.showCampaignHint(x + PALETTE_CARD_W / 2, y);
          return;
        }
        if (ProgressionManager.canUnlockComponent(type)) {
          this.unlockAnimPlaying = true;
          if (ProgressionManager.tryUnlockComponent(type)) {
            this.playCardUnlockAnimation(type, x, y, catColor, def, lockedElements);
          } else {
            this.unlockAnimPlaying = false;
          }
        } else {
          SoundManager.play('error');
          this.playCannotAffordFeedback(lockedElements, costLabel);
        }
      });

      bg.on('pointerover', () => {
        const tooltipX = x + PALETTE_CARD_W + 5;
        this.callbacks.showTooltip(tooltipX, this.paletteY - 90, def, true);
      });
      bg.on('pointerout', () => {
        this.callbacks.hideTooltip();
      });

      return;
    }

    // ── Unlocked component card ──
    const unlockedCompName = t(`component.${def.type}.name`);
    const shortName = unlockedCompName.length > 12 ? unlockedCompName.substring(0, 11) + '.' : unlockedCompName;
    const nameText = this.scene.add.text(x + PALETTE_CARD_W / 2, y + 60, shortName, {
      fontSize: '11px', color: '#ccccdd', fontFamily: 'monospace',
    }).setOrigin(0.5, 0);
    this.paletteContainer.add(nameText);

    const levelCost = def.costPerLevel[Math.min(evoLevel, 2)];
    const displayCost = GOD.freeBuilding ? 0 : Math.round(levelCost * GOD.componentCostMult);
    const costText = this.scene.add.text(x + PALETTE_CARD_W / 2, y + 76, `$${displayCost}`, {
      fontSize: '12px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5, 0);
    this.paletteContainer.add(costText);

    if (def.maxEvolutionLevel > 0) {
      const maxUnlocked = ProgressionManager.getComponentLevel(type);
      const maxed = maxUnlocked >= def.maxEvolutionLevel;

      const lvlColor = (evoLevel >= def.maxEvolutionLevel) ? '#ffcc44' : '#88aaff';
      const lvlBadge = this.scene.add.text(x + PALETTE_CARD_W - 4, y + 2, evoLevel === 0 ? 'Base' : `Lv.${evoLevel + 1}`, {
        fontSize: '9px', color: lvlColor, fontFamily: 'monospace', fontStyle: 'bold',
      }).setOrigin(1, 0);
      this.paletteContainer.add(lvlBadge);

      if (!maxed && this.isCampaign && evoLevel === maxUnlocked) {
        const btnSize = 20;
        const ax = x + 2;
        const ay = y + 2;
        const upgradeImg = this.scene.add.image(ax + btnSize / 2, ay + btnSize / 2, 'icon_upgrade')
          .setDisplaySize(btnSize, btnSize)
          .setInteractive({ useHandCursor: true }).setDepth(2);
        this.paletteContainer.add(upgradeImg);

        this.scene.tweens.add({
          targets: upgradeImg,
          alpha: 0.5, yoyo: true, repeat: -1, duration: 800, ease: 'Sine.easeInOut',
        });

        upgradeImg.on('pointerdown', (p: Phaser.Input.Pointer) => {
          p.event.stopPropagation();
          SoundManager.play('click');
          this.openUpgradeDialog(type, x + PALETTE_CARD_W / 2, y + PALETTE_CARD_H / 2);
        });
      }
    }

    const cardKey = `${type}:${evoLevel}`;
    this.paletteHighlights.set(cardKey, bg);

    bg.on('pointerover', () => {
      if (this.selectedType !== type || this.selectedEvoLevel !== evoLevel) bg.setFillStyle(0x222248);
      const tooltipX = x + PALETTE_CARD_W + 5;
      this.callbacks.showTooltip(tooltipX, this.paletteY - 90, def, false, evoLevel);
    });
    bg.on('pointerout', () => {
      if (this.selectedType !== type || this.selectedEvoLevel !== evoLevel) bg.setFillStyle(0x151530);
      this.callbacks.hideTooltip();
    });

    bg.on('pointerdown', () => {
      SoundManager.play('clickSelect');
      this.selectPaletteItem(type, evoLevel);
    });
  }

  // ── Cannot Afford Feedback ──

  private playCannotAffordFeedback(
    lockedElements: Phaser.GameObjects.GameObject[],
    costLabel: Phaser.GameObjects.Text,
  ): void {
    for (const el of lockedElements) {
      const obj = el as Phaser.GameObjects.Components.Transform & Phaser.GameObjects.GameObject;
      if (!obj.x) continue;
      const origX = obj.x;
      this.scene.tweens.add({
        targets: obj,
        x: origX + 4,
        duration: 40,
        yoyo: true,
        repeat: 3,
        ease: 'Sine.inOut',
        onComplete: () => { obj.x = origX; },
      });
    }
    costLabel.setColor('#ff2222');
    this.scene.time.delayedCall(350, () => costLabel.setColor('#ff8844'));

    const bg = lockedElements[0] as Phaser.GameObjects.Rectangle;
    if (bg?.setFillStyle) {
      bg.setFillStyle(0x331111, 0.9);
      this.scene.time.delayedCall(350, () => bg.setFillStyle(0x0e0e1a, 0.7));
    }

    this.lockedFailStreak++;
    const tiers = [
      [t('funds.0.0'), t('funds.0.1'), t('funds.0.2'), t('funds.0.3')],
      [t('funds.1.0'), t('funds.1.1'), t('funds.1.2'), t('funds.1.3'), t('funds.1.4')],
      [t('funds.2.0'), t('funds.2.1'), t('funds.2.2'), t('funds.2.3'), t('funds.2.4'), t('funds.2.5')],
      [t('funds.3.0'), t('funds.3.1'), t('funds.3.2'), t('funds.3.3'), t('funds.3.4'), t('funds.3.5'), t('funds.3.6')],
      [t('funds.4.0'), t('funds.4.1'), t('funds.4.2'), t('funds.4.3'), t('funds.4.4'), t('funds.4.5'), t('funds.4.6'), t('funds.4.7')],
    ];
    const tierIdx = this.lockedFailStreak <= 3 ? 0
      : this.lockedFailStreak <= 6 ? 1
      : this.lockedFailStreak <= 10 ? 2
      : this.lockedFailStreak <= 15 ? 3 : 4;
    const pool = tiers[tierIdx];
    const msg = pool[Math.floor(Math.random() * pool.length)];
    const fontSize = Math.min(22, 12 + this.lockedFailStreak * 0.5);

    const px = costLabel.x + (this.paletteContainer?.x ?? 0);
    const py = costLabel.y + (this.paletteContainer?.y ?? 0) - 20;
    const popup = this.scene.add.text(px, py, msg, {
      fontSize: `${fontSize}px`, fontFamily: 'monospace', fontStyle: 'bold',
      color: '#ff4444', stroke: '#000000', strokeThickness: 3,
      align: 'center',
    }).setOrigin(0.5).setDepth(600);

    this.scene.tweens.add({
      targets: popup,
      y: py - 45,
      alpha: 0,
      duration: 1600,
      ease: 'Cubic.easeOut',
      onComplete: () => popup.destroy(),
    });
  }

  // ── Unlock Animation ──

  private playCardUnlockAnimation(
    type: ComponentType, x: number, y: number, _catColor: number,
    def: ComponentDef,
    lockedElements: Phaser.GameObjects.GameObject[],
  ): void {
    const cx = x + PALETTE_CARD_W / 2;
    const cy = y + PALETTE_CARD_H / 2;

    SoundManager.play('componentUnlock');

    const lockEl = lockedElements[3];
    if (lockEl && (lockEl as Phaser.GameObjects.Image).setAngle) {
      const lockImg = lockEl as Phaser.GameObjects.Image;
      this.scene.tweens.add({
        targets: lockImg,
        angle: { from: -12, to: 12 },
        duration: 60,
        yoyo: true,
        repeat: 4,
        onComplete: () => {
          this.scene.tweens.add({
            targets: lockImg,
            scaleX: 2, scaleY: 2, alpha: 0, angle: 25,
            duration: 300, ease: 'Back.easeIn',
            onComplete: () => lockImg.destroy(),
          });
        },
      });
    }

    const otherLocked = lockedElements.filter((_, i) => i !== 3);
    this.scene.tweens.add({
      targets: otherLocked,
      alpha: 0,
      duration: 300,
      delay: 300,
      onComplete: () => otherLocked.forEach(el => el.destroy()),
    });

    this.scene.time.delayedCall(500, () => {
      const screenX = cx + (this.paletteContainer?.x ?? 0);
      const screenY = cy + (this.paletteContainer?.y ?? 0);
      JuiceEffects.componentUnlockEffect(this.scene, screenX, screenY);

      const unlockPrice = Math.round(def.unlockCost * GOD.workshopPriceMult);
      if (!GOD.freeBuilding) this.callbacks.spawnCostAnim(-unlockPrice, screenX, screenY);

      const label = this.scene.add.text(screenX, screenY - 20, 'UNLOCKED!', {
        fontSize: '13px', color: '#88aaff', fontFamily: 'monospace', fontStyle: 'bold',
        stroke: '#111133', strokeThickness: 3,
      }).setOrigin(0.5).setDepth(50);
      this.scene.tweens.add({
        targets: label,
        y: screenY - 50, alpha: 0,
        duration: 900, ease: 'Quad.easeOut',
        onComplete: () => label.destroy(),
      });

      this.unlockAnimPlaying = false;
      this.rebuildPalette();
    });
  }

  // ── Upgrade Dialog ──

  openUpgradeDialog(type: ComponentType, cardCx: number, cardCy: number): void {
    this.closeUpgradeDialog();

    const def = COMPONENT_DEFS[type];
    const level = ProgressionManager.getComponentLevel(type);
    const rawCost = Math.round(getEvolutionCost(level, def.maxEvolutionLevel, type) * GOD.workshopPriceMult);
    const cost = GOD.freeBuilding ? 0 : rawCost;
    const canAfford = ProgressionManager.canEvolveComponent(type);
    const currentMult = getEvolutionMultiplier(level);
    const nextMult = getEvolutionMultiplier(level + 1);
    const pctGain = Math.round(EVOLUTION_STAT_MULTIPLIER * 100);

    const cx = CANVAS_WIDTH / 2;
    const cy = CANVAS_HEIGHT / 2;
    this.upgradeDialog = this.scene.add.container(cx, cy).setDepth(200);

    const dim = this.scene.add.rectangle(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT, 0x000000, 0.6)
      .setInteractive();
    dim.on('pointerdown', () => this.closeUpgradeDialog());
    this.upgradeDialog.add(dim);

    const statLines: string[] = [];
    const addStat = (label: string, base: number) => {
      if (base <= 0) return;
      const cur = +(base * currentMult).toFixed(1);
      const nxt = +(base * nextMult).toFixed(1);
      statLines.push(`${label}: ${cur} \u2192 ${nxt}`);
    };
    addStat('HP', def.hp);
    addStat('Energy', def.energy);
    addStat('Damage', def.damage);
    addStat('Fire Rate', def.fireRate);
    const diagUnit = TILE_SIZE * Math.SQRT2;
    const addStatDiag = (label: string, base: number) => {
      if (base <= 0) return;
      const cur = (base * currentMult / diagUnit).toFixed(1);
      const nxt = (base * nextMult / diagUnit).toFixed(1);
      statLines.push(`${label}: ${cur}D \u2192 ${nxt}D`);
    };
    addStatDiag('Range', def.weaponRange);
    addStatDiag('Sensor', def.sensorRange);
    addStat('Repair', def.repairRate);
    addStat('Recharge', def.rechargeRate);

    const pw = 300;
    const statsBlockH = 14 + statLines.length * 13 + 4;
    const ph = 130 + 66 + statsBlockH + 56;
    const panelGfx = this.scene.add.graphics();
    panelGfx.fillStyle(0x0a0a1e, 0.95);
    panelGfx.fillRoundedRect(-pw / 2, -ph / 2, pw, ph, 8);
    drawTechRect(panelGfx, -pw / 2, -ph / 2, pw, ph, 0x44ff88, 0.7, 1.5);
    this.upgradeDialog.add(panelGfx);

    const blocker = this.scene.add.rectangle(0, 0, pw, ph, 0x000000, 0).setInteractive();
    this.upgradeDialog.add(blocker);

    let ly = -ph / 2 + 16;

    this.upgradeDialog.add(this.scene.add.text(0, ly, `UNLOCK TIER ${level + 2} \u2014 ${t(`component.${def.type}.name`)}`, {
      fontSize: '13px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5));
    ly += 22;

    this.upgradeDialog.add(this.scene.add.text(0, ly, `Tier ${level + 1}  \u2192  Tier ${level + 2}`, {
      fontSize: '13px', color: '#44ff88', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5));
    ly += 22;

    const imgSize = 56;
    const imgY = ly + imgSize / 2;
    const curTexKey = getComponentTextureKeyAtLevel(type, level);
    const nxtTexKey = getComponentTextureKeyAtLevel(type, level + 1);
    if (curTexKey && this.scene.textures.exists(curTexKey)) {
      const curImg = this.scene.add.image(-40, imgY, curTexKey).setOrigin(0.5);
      const curScale = imgSize / Math.max(curImg.width, curImg.height);
      curImg.setScale(curScale);
      this.upgradeDialog.add(curImg);
    }
    this.upgradeDialog.add(this.scene.add.text(0, imgY, '\u2192', {
      fontSize: '16px', color: '#44ff88', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5));
    if (nxtTexKey && this.scene.textures.exists(nxtTexKey)) {
      const nxtImg = this.scene.add.image(40, imgY, nxtTexKey).setOrigin(0.5);
      const nxtScale = imgSize / Math.max(nxtImg.width, nxtImg.height);
      nxtImg.setScale(nxtScale);
      this.upgradeDialog.add(nxtImg);
      this.scene.tweens.add({
        targets: nxtImg,
        alpha: 0.6, yoyo: true, repeat: -1, duration: 600, ease: 'Sine.easeInOut',
      });
    }
    ly += imgSize + 10;

    const massPct = Math.round(GOD.tierMassMult * (level + 1) * 100);
    const drainPct = Math.round(GOD.tierEnergyDrainMult * (level + 1) * 100);
    statLines.push(`Mass: +${massPct}% (tradeoff)`);
    if (drainPct > 0) statLines.push(`Energy drain: +${drainPct}% (tradeoff)`);

    this.upgradeDialog.add(this.scene.add.text(0, ly, `+${pctGain}% stats, but heavier:`, {
      fontSize: '10px', color: '#88aacc', fontFamily: 'monospace',
    }).setOrigin(0.5));
    ly += 14;

    this.upgradeDialog.add(this.scene.add.text(-pw / 2 + 20, ly, statLines.join('\n'), {
      fontSize: '10px', color: '#ccddee', fontFamily: 'monospace', lineSpacing: 3,
    }));
    ly += statLines.length * 13 + 10;

    const resCost = getEvolutionResourceCost(type, level);
    const canAffordCo = ResourceManager.co >= resCost.co;
    const canAffordElectro = ResourceManager.electro >= resCost.electro;
    const canAffordCurrency = GOD.freeBuilding || ProgressionManager.getCurrency() >= cost;
    const canAffordAll = canAfford;

    const costY = ly;
    // Currency cost
    const currColor = canAffordCurrency ? '#ffcc00' : '#ff4444';
    this.upgradeDialog.add(this.scene.add.text(0, costY, `$${cost}`, {
      fontSize: '13px', color: currColor, fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5));

    // Resource costs (Co + Electro) on one line below
    if (resCost.co > 0 || resCost.electro > 0) {
      const resY = costY + 18;
      const parts: { text: string; color: string; icon: string }[] = [];
      if (resCost.co > 0) parts.push({ text: `${resCost.co}`, color: canAffordCo ? '#44ee88' : '#ff4444', icon: 'icon_co' });
      if (resCost.electro > 0) parts.push({ text: `${resCost.electro}`, color: canAffordElectro ? '#88ccff' : '#ff4444', icon: 'icon_electro' });
      const icoSz = 14;
      const gap = 6;
      // Measure total width to center
      let totalW = 0;
      for (const p of parts) {
        totalW += icoSz + 3 + p.text.length * 7.2; // approx monospace width
        if (totalW > 0) totalW += gap;
      }
      let rx = -totalW / 2;
      for (const p of parts) {
        const ico = this.scene.add.image(rx + icoSz / 2, resY, p.icon).setDisplaySize(icoSz, icoSz).setDepth(201);
        rx += icoSz + 3;
        const txt = this.scene.add.text(rx, resY, p.text, {
          fontSize: '11px', color: p.color, fontFamily: 'monospace', fontStyle: 'bold',
        }).setOrigin(0, 0.5).setDepth(201);
        rx += txt.width + gap;
        this.upgradeDialog.add(ico);
        this.upgradeDialog.add(txt);
      }
    }

    if (!canAffordAll) {
      const notEnoughY = costY + (resCost.co > 0 || resCost.electro > 0 ? 34 : 16);
      this.upgradeDialog.add(this.scene.add.text(0, notEnoughY, 'Not enough resources', {
        fontSize: '9px', color: '#ff6666', fontFamily: 'monospace',
      }).setOrigin(0.5));
    }

    const btnY = ph / 2 - 24;
    const confirmBtn = createSmallTechButton(this.scene, -55, btnY, 'UNLOCK', canAfford ? 0x44ff88 : 0x333355, () => {
      if (!canAfford) { SoundManager.play('error'); return; }
      if (ProgressionManager.tryEvolveComponent(type)) {
        SoundManager.play('evolve');
        const screenX = cardCx + (this.paletteContainer?.x ?? 0);
        const screenY = cardCy + (this.paletteContainer?.y ?? 0);
        JuiceEffects.componentEvolveEffect(this.scene, screenX, screenY);
        this.closeUpgradeDialog();
        const snap = this.callbacks.snapshotEditData();
        this.scene.time.delayedCall(600, () => this.scene.scene.restart(snap));
      } else {
        SoundManager.play('error');
      }
    });
    this.upgradeDialog.add(confirmBtn);

    const cancelBtn = createSmallTechButton(this.scene, 55, btnY, 'CANCEL', TECH.DIM, () => {
      SoundManager.play('clickCancel');
      this.closeUpgradeDialog();
    });
    this.upgradeDialog.add(cancelBtn);

    this.upgradeDialog.setScale(0.5).setAlpha(0);
    this.scene.tweens.add({
      targets: this.upgradeDialog,
      scaleX: 1, scaleY: 1, alpha: 1,
      duration: 200, ease: 'Back.easeOut',
    });
  }

  closeUpgradeDialog(): void {
    if (this.upgradeDialog) {
      this.upgradeDialog.destroy();
      this.upgradeDialog = null;
    }
  }

  selectPaletteItem(type: ComponentType, evoLevel = 0): void {
    this.selectedType = type;
    this.selectedEvoLevel = evoLevel;
    const activeKey = `${type}:${evoLevel}`;
    for (const [key, rect] of this.paletteHighlights) {
      if (key === activeKey) {
        rect.setFillStyle(0x2a2a55);
        rect.setStrokeStyle(2, 0x6688cc);
      } else {
        rect.setFillStyle(0x151530);
        rect.setStrokeStyle(1, 0x333355, 0.6);
      }
    }
  }
}
